import { gql } from '@apollo/client/core';

// ── Fragments ──

export const SETTLEMENT_ACCOUNT_FIELDS = gql`
  fragment SettlementAccountFields on SettlementAccount {
    id
    school_id
    provider_name
    sub_account_id
    account_name
    account_number
    bank_code
    bank_name
    is_active
    created_at
  }
`;

export const FEE_SPLIT_RULE_FIELDS = gql`
  fragment FeeSplitRuleFields on FeeSplitRule {
    id
    fee_split_profile_id
    bill_item_template_id
    bill_item_category_id
    billable_type
    billable_id
    hq_amount
    campus_amount
    hq_percentage
    campus_percentage
    base_amount
    campus_price
    priority
    billItemTemplate {
      id
      name
    }
    billItemCategory {
      id
      name
    }
  }
`;

export const FEE_SPLIT_PROFILE_FIELDS = gql`
  fragment FeeSplitProfileFields on FeeSplitProfile {
    id
    name
    description
    split_mode
    is_default
    is_active
    created_at
    rules {
      ...FeeSplitRuleFields
    }
    assignments {
      id
      fee_split_profile_id
      child_school_id
      is_active
      childSchool {
        id
        name
      }
      overrides {
        id
        fee_split_assignment_id
        fee_split_rule_id
        hq_amount
        campus_amount
        hq_percentage
        campus_percentage
        base_amount
        campus_price
      }
    }
  }
  ${FEE_SPLIT_RULE_FIELDS}
`;

// ── Queries ──

export const GET_FEE_SPLIT_PROFILES = gql`
  query GetFeeSplitProfiles($is_active: Boolean) {
    feeSplitProfiles(is_active: $is_active) {
      ...FeeSplitProfileFields
    }
  }
  ${FEE_SPLIT_PROFILE_FIELDS}
`;

export const GET_SETTLEMENT_ACCOUNTS = gql`
  query GetSettlementAccounts($provider_name: String) {
    settlementAccounts(provider_name: $provider_name) {
      ...SettlementAccountFields
    }
  }
  ${SETTLEMENT_ACCOUNT_FIELDS}
`;

export const GET_SPLIT_SUMMARY = gql`
  query GetSplitSummary($child_school_id: ID, $date_from: Date, $date_to: Date) {
    splitSummary(child_school_id: $child_school_id, date_from: $date_from, date_to: $date_to) {
      total_amount
      total_hq_amount
      total_campus_amount
      record_count
      by_school {
        school_id
        school_name
        total_amount
        hq_amount
        campus_amount
        record_count
      }
    }
  }
`;

export const GET_PAYMENT_SPLIT_RECORDS = gql`
  query GetPaymentSplitRecords($child_school_id: ID, $date_from: Date, $date_to: Date, $page: Int, $per_page: Int) {
    paymentSplitRecords(child_school_id: $child_school_id, date_from: $date_from, date_to: $date_to, page: $page, per_page: $per_page) {
      data {
        id
        total_amount
        hq_amount
        campus_amount
        split_mode
        gateway_split_status
        created_at
        student_name
        student_matric_no
        payment_reference
        campusSchool {
          id
          name
        }
      }
      total
      current_page
      per_page
    }
  }
`;

// ── Mutations ──

export const CREATE_FEE_SPLIT_PROFILE = gql`
  mutation CreateFeeSplitProfile($input: CreateFeeSplitProfileInput!) {
    createFeeSplitProfile(input: $input) {
      ...FeeSplitProfileFields
    }
  }
  ${FEE_SPLIT_PROFILE_FIELDS}
`;

export const UPDATE_FEE_SPLIT_PROFILE = gql`
  mutation UpdateFeeSplitProfile($input: UpdateFeeSplitProfileInput!) {
    updateFeeSplitProfile(input: $input) {
      ...FeeSplitProfileFields
    }
  }
  ${FEE_SPLIT_PROFILE_FIELDS}
`;

export const DELETE_FEE_SPLIT_PROFILE = gql`
  mutation DeleteFeeSplitProfile($id: ID!) {
    deleteFeeSplitProfile(id: $id)
  }
`;

export const CREATE_FEE_SPLIT_RULE = gql`
  mutation CreateFeeSplitRule($input: CreateFeeSplitRuleInput!) {
    createFeeSplitRule(input: $input) {
      ...FeeSplitRuleFields
    }
  }
  ${FEE_SPLIT_RULE_FIELDS}
`;

export const UPDATE_FEE_SPLIT_RULE = gql`
  mutation UpdateFeeSplitRule($input: UpdateFeeSplitRuleInput!) {
    updateFeeSplitRule(input: $input) {
      ...FeeSplitRuleFields
    }
  }
  ${FEE_SPLIT_RULE_FIELDS}
`;

export const DELETE_FEE_SPLIT_RULE = gql`
  mutation DeleteFeeSplitRule($id: ID!) {
    deleteFeeSplitRule(id: $id)
  }
`;

export const ASSIGN_FEE_SPLIT_PROFILE = gql`
  mutation AssignFeeSplitProfile($input: AssignFeeSplitProfileInput!) {
    assignFeeSplitProfile(input: $input) {
      id
      fee_split_profile_id
      child_school_id
      is_active
      childSchool {
        id
        name
      }
    }
  }
`;

export const UNASSIGN_FEE_SPLIT_PROFILE = gql`
  mutation UnassignFeeSplitProfile($id: ID!) {
    unassignFeeSplitProfile(id: $id)
  }
`;

export const CREATE_SETTLEMENT_ACCOUNT = gql`
  mutation CreateSettlementAccount($input: CreateSettlementAccountInput!) {
    createSettlementAccount(input: $input) {
      ...SettlementAccountFields
    }
  }
  ${SETTLEMENT_ACCOUNT_FIELDS}
`;

export const UPDATE_SETTLEMENT_ACCOUNT = gql`
  mutation UpdateSettlementAccount($input: UpdateSettlementAccountInput!) {
    updateSettlementAccount(input: $input) {
      ...SettlementAccountFields
    }
  }
  ${SETTLEMENT_ACCOUNT_FIELDS}
`;

export const DELETE_SETTLEMENT_ACCOUNT = gql`
  mutation DeleteSettlementAccount($id: ID!) {
    deleteSettlementAccount(id: $id)
  }
`;

export const CREATE_FEE_SPLIT_OVERRIDE = gql`
  mutation CreateFeeSplitOverride($input: CreateFeeSplitOverrideInput!) {
    createFeeSplitOverride(input: $input) {
      id
      fee_split_assignment_id
      fee_split_rule_id
      hq_amount
      campus_amount
      hq_percentage
      campus_percentage
      base_amount
      campus_price
    }
  }
`;

export const UPDATE_FEE_SPLIT_OVERRIDE = gql`
  mutation UpdateFeeSplitOverride($input: UpdateFeeSplitOverrideInput!) {
    updateFeeSplitOverride(input: $input) {
      id
      hq_amount
      campus_amount
      hq_percentage
      campus_percentage
      base_amount
      campus_price
    }
  }
`;

export const DELETE_FEE_SPLIT_OVERRIDE = gql`
  mutation DeleteFeeSplitOverride($id: ID!) {
    deleteFeeSplitOverride(id: $id)
  }
`;

/**
 * Hierarchy + opt-in flags for the current school. Fee splitting is dual
 * opt-in: a parent enables it for the organisation AND each child school
 * consents — nothing splits until both flags are on.
 */
export const GET_FEE_SPLIT_FLAGS = gql`
  query GetFeeSplitFlags {
    getSchool {
      id
      is_child
      fee_split_enabled
      parent_features_enabled
      parentSchool {
        id
        name
        fee_split_enabled
      }
    }
  }
`;

export const SET_FEE_SPLIT_OPT_IN = gql`
  mutation SetFeeSplitOptIn($enabled: Boolean!) {
    setFeeSplitOptIn(enabled: $enabled) {
      id
      fee_split_enabled
    }
  }
`;
